import { FC, ReactNode } from 'react';
import { Avatar, Button, Checkbox, Col, Divider, Modal, Row } from 'antd';
import { ListFilter } from 'lucide-react';
import { TbBuilding, TbBuildingFactory2, TbHistory, TbLayersIntersect, TbRefresh } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import Select from '@/shared/components/forms/PlatformSelect';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import { preferenceModalBodyStyle } from '@/shared/components/modals/preferenceModalStyles';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';
import { useUserPreferences } from '@/stores/database/entities/00-RBAC/99-UserPreferences';

import { defaultSearchPreferences, resolveSearchPreferences, SearchPreferences } from '../../preferences';
import { isSearchTabEnabled, searchTabDefinitions, SearchTabKey } from '../../searchTabs';

interface ManageSearchModalProps {
    open: boolean;
    setOpen: (open: boolean) => void;
}

interface PreferenceRowProps {
    checked?: boolean;
    description: string;
    disabled?: boolean;
    icon: ReactNode;
    label: string;
    onChange?: (checked: boolean) => void;
    trailing?: ReactNode;
}

const PreferenceRow: FC<PreferenceRowProps> = ({
    checked,
    description,
    disabled,
    icon,
    label,
    onChange,
    trailing,
}): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <Row align='middle' gutter={[8, 0]} style={{ margin: '0.25rem 0', padding: '0.25rem' }} wrap={false}>
            {onChange && (
                <Col flex='none'>
                    <Checkbox
                        aria-label={label}
                        checked={checked}
                        disabled={disabled}
                        onChange={(event) => onChange(event.target.checked)}
                    />
                </Col>
            )}
            <Col flex='none'>
                <Avatar
                    icon={icon}
                    shape='square'
                    size={24}
                    style={{
                        backgroundColor: token.colorBgContainer,
                        border: `1px solid ${token.colorBorder}`,
                        color: token.colorTextTertiary,
                    }}
                />
            </Col>
            <Col flex='auto' style={{ minWidth: 0 }}>
                <span style={{ color: token.colorTextSecondary }}>{label}</span>
                <small
                    style={{
                        color: token.colorTextTertiary,
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {description}
                </small>
            </Col>
            {trailing && <Col flex='none'>{trailing}</Col>}
        </Row>
    );
};

const ManageSearchModal: FC<ManageSearchModalProps> = ({ open, setOpen }): JSX.Element => {
    const { preferences, setPreference } = useUserPreferences();
    const searchPreferences = resolveSearchPreferences(preferences?.search as SearchPreferences | undefined);
    const enabledTabs = searchTabDefinitions.filter((tab) => isSearchTabEnabled(tab, searchPreferences.tabs));
    const defaultTab =
        searchTabDefinitions.find((tab) => tab.key === searchPreferences.defaultTab) ?? searchTabDefinitions[0];
    const { token } = useAntDesignTheme();

    const resetSection = (section: keyof typeof defaultSearchPreferences) => {
        if (section === 'tabs') {
            setPreference('search.defaultTab', defaultSearchPreferences.defaultTab);
        }

        setPreference(`search.${section}`, defaultSearchPreferences[section]);
    };

    const sectionHeader = (label: string, section: keyof typeof defaultSearchPreferences) => (
        <Row align='middle' gutter={[8, 0]} style={{ marginBottom: '0.25rem' }} wrap={false}>
            <Col flex='auto'>
                <small style={{ color: token.colorTextTertiary }}>{label}</small>
            </Col>
            <Col flex='none'>
                <SmallTooltip padding='0 0.25rem' placement='left' title={`Reset ${label}`}>
                    <Button onClick={() => resetSection(section)} size='small' type='text'>
                        <TbRefresh style={{ color: token.colorTextTertiary }} />
                    </Button>
                </SmallTooltip>
            </Col>
        </Row>
    );

    const sectionStyle = {
        backgroundColor: token.colorBgBase,
        border: `1px dashed ${token.colorBorder}`,
        borderRadius: token.borderRadius,
        padding: '0.25rem',
    };

    return (
        <Modal
            destroyOnClose
            footer={
                <Row>
                    <Col flex='none'>
                        <LearnMoreFooter align='left' topic='search preferences and result discovery' />
                    </Col>
                    <Col flex='auto' />
                </Row>
            }
            onCancel={() => setOpen(false)}
            open={open}
            styles={{ body: { ...preferenceModalBodyStyle, maxHeight: '70vh', overflowY: 'auto' } }}
            title='Manage Search'
            width={16 * 34}
        >
            {sectionHeader('Search Tabs', 'tabs')}
            <div style={sectionStyle}>
                <PreferenceRow
                    description='Choose the tab opened when navigating directly to Search.'
                    icon={<defaultTab.Icon size='0.8rem' />}
                    label='Default Search Tab'
                    trailing={
                        <Select
                            aria-label='Default Search Tab'
                            onChange={(value: SearchTabKey) => setPreference('search.defaultTab', value)}
                            options={enabledTabs.map((tab) => ({ label: tab.label, value: tab.key }))}
                            size='small'
                            style={{ width: '8rem' }}
                            value={searchPreferences.defaultTab}
                        />
                    }
                />
                {searchTabDefinitions.map((tab) => (
                    <PreferenceRow
                        checked={isSearchTabEnabled(tab, searchPreferences.tabs)}
                        description={tab.description}
                        disabled={enabledTabs.length === 1 && isSearchTabEnabled(tab, searchPreferences.tabs)}
                        icon={<tab.Icon size='0.8rem' />}
                        key={tab.key}
                        label={tab.label}
                        onChange={(checked) => {
                            if (!checked && tab.key === searchPreferences.defaultTab) {
                                const nextDefaultTab = enabledTabs.find((enabledTab) => enabledTab.key !== tab.key);

                                if (nextDefaultTab) {
                                    setPreference('search.defaultTab', nextDefaultTab.key);
                                }
                            }

                            setPreference(`search.tabs.${tab.key}`, checked);
                        }}
                    />
                ))}
            </div>

            <Divider style={{ margin: '0.75rem 0' }} />
            {sectionHeader('Result Sources & Display', 'results')}
            <div style={sectionStyle}>
                <PreferenceRow
                    checked={searchPreferences.results.includePublicCompanies}
                    description='Include public-market companies supplied by platform data providers.'
                    icon={<TbBuildingFactory2 size='0.8rem' />}
                    label='Public Companies'
                    onChange={(checked) => setPreference('search.results.includePublicCompanies', checked)}
                />
                <PreferenceRow
                    checked={searchPreferences.results.groupRelatedListings}
                    description='Group related securities and alternate listings beneath a primary result.'
                    icon={<TbLayersIntersect size='0.8rem' />}
                    label='Group Related Listings'
                    onChange={(checked) => setPreference('search.results.groupRelatedListings', checked)}
                />
                <PreferenceRow
                    checked={searchPreferences.results.showRecentSearches}
                    description='Show recent queries when no search term has been entered.'
                    icon={<TbHistory size='0.8rem' />}
                    label='Recent Searches'
                    onChange={(checked) => setPreference('search.results.showRecentSearches', checked)}
                />
                <PreferenceRow
                    description='Choose how many results are shown before pagination or loading more.'
                    icon={<TbBuilding size='0.8rem' />}
                    label='Results Per Page'
                    trailing={
                        <Select
                            onChange={(value: 10 | 25 | 50) => setPreference('search.results.pageSize', value)}
                            options={[10, 25, 50].map((value) => ({ label: value, value }))}
                            size='small'
                            style={{ width: '5rem' }}
                            value={searchPreferences.results.pageSize}
                        />
                    }
                />
            </div>

            <Divider style={{ margin: '0.75rem 0' }} />
            {sectionHeader('Filtering', 'filters')}
            <div style={sectionStyle}>
                <PreferenceRow
                    checked={searchPreferences.filters.preserveOnTabChange}
                    description='Keep the active company filters when navigating between Search tabs.'
                    icon={<ListFilter size='0.75rem' />}
                    label='Preserve Applied Filters'
                    onChange={(checked) => setPreference('search.filters.preserveOnTabChange', checked)}
                />
            </div>

            <Divider style={{ margin: '0.75rem 0' }} />
            <Button block onClick={() => setPreference('search', defaultSearchPreferences)} type='dashed'>
                <TbRefresh />
                Reset All Search Preferences
            </Button>
        </Modal>
    );
};

export default ManageSearchModal;
